import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Popup, useMap } from 'react-leaflet';
import ZoomIndependentMarker from '../ZoomIndependentMarker';
import { setSelectedDevice, clearSelectedDevice } from '../../store/slices/devicesSlice';
import { fetchDeviceHistory, fetchLgBandTodayReadingsHistory, clearHistory, startLiveSession } from '../../store/slices/deviceHistorySlice';
import { addAlert } from '../../store/slices/uiSlice';
import { isLgBandSosDevice } from '../../utils/lgBandSos';
import L from 'leaflet';

/** LG Band / Adafruit map devices: single-click selection + pulse; other assets use double-click. */
function isLgBandDeviceType(device) {
  if (!device?.device_type) return false;
  const t = String(device.device_type).toLowerCase();
  return t === 'lg_band' || t === 'adafruit';
}

// It's more efficient to define icons outside the component render cycle.
const iconSize = [64, 64];
const iconAnchor = [32, 64];
const popupAnchor = [0, -60];

// Match standard vehicle markers (64×64) for consistent on-map scale
const lgBandIconSize = [64, 64];
const lgBandIconAnchor = [32, 64];
const lgBandPopupAnchor = [0, -60];

const LG_BAND_MARKER_PX = 64;
/** Canonical map asset — public/LG_Band.png (not LG_Band_3 / LG_Band_main variants). */
const LG_BAND_ICON_URL = '/LG_Band.png?v=lg-band-main';

const ICONS = {
  mobile: new L.Icon({ iconUrl: '/mobile_logo.png', iconSize, iconAnchor, popupAnchor }),
  triton: new L.Icon({ iconUrl: '/triton_3.png', iconSize, iconAnchor, popupAnchor }),
  lg_band: new L.Icon({ iconUrl: LG_BAND_ICON_URL, iconSize: lgBandIconSize, iconAnchor: lgBandIconAnchor, popupAnchor: lgBandPopupAnchor }),
  aurus: new L.Icon({ iconUrl: '/aurus.png', iconSize, iconAnchor, popupAnchor }),
  ebike: new L.Icon({ iconUrl: '/ebike.png', iconSize, iconAnchor, popupAnchor }),
  vehicle: new L.Icon({ iconUrl: '/vehicle.png', iconSize, iconAnchor, popupAnchor }),
  boat: new L.Icon({ iconUrl: '/boat.png', iconSize: [56, 72], iconAnchor: [28, 72], popupAnchor: [0, -72] }),
  truck: new L.Icon({ iconUrl: '/truck.png', iconSize: [60, 68], iconAnchor: [30, 68], popupAnchor: [0, -68] }),
  bike: new L.Icon({ iconUrl: '/bike.png', iconSize, iconAnchor, popupAnchor }),
  jeep: new L.Icon({ iconUrl: '/jeep.png', iconSize, iconAnchor, popupAnchor }),
  atv: new L.Icon({ iconUrl: '/vehicle.png', iconSize, iconAnchor, popupAnchor }),
  seahorse: new L.Icon({ iconUrl: '/s_icon.png', iconSize, iconAnchor, popupAnchor }),
  default: new L.Icon.Default(),
};

const getDeviceIcon = (deviceType) => {
  if (!deviceType) {
    return ICONS.default;
  }
  
  // Normalize device type to lowercase for matching
  const normalizedType = String(deviceType).toLowerCase().trim();
  
  // Special handling for boat-related types - check if contains 'boat' anywhere
  if (normalizedType.includes('boat') || normalizedType.includes('sail') || normalizedType === 'ship' || normalizedType === 'vessel') {
    return ICONS.boat;
  }
  
  // Map vehicle types to icons
  const typeMapping = {
    'jeep': 'jeep',
    'car': 'jeep', // Cars use jeep icon
    'truck': 'truck',
    'mini tempo': 'truck',
    'minitempo': 'truck',
    'hiab truck': 'truck',
    'bike': 'bike',
    'motorcycle': 'bike',
    'scooter': 'bike',
    'mobile': 'mobile',
    'triton': 'triton',
    'lg_band': 'lg_band',
    'aurus': 'aurus',
    'ebike': 'ebike',
    'vehicle': 'vehicle',
    'atv': 'atv',
    'boat': 'boat',
    'sailboat': 'boat',
    'sailingship': 'boat',
    'sailing ship': 'boat',
    'ship': 'boat',
    'vessel': 'boat',
    'seahorse': 'seahorse',
  };
  
  // Check direct mapping first
  if (ICONS[normalizedType]) {
    return ICONS[normalizedType];
  }
  
  // Check type mapping
  const mappedType = typeMapping[normalizedType];
  if (mappedType && ICONS[mappedType]) {
    return ICONS[mappedType];
  }
  
  // Fallback to default
  return ICONS.default;
};

const truncateDeviceName = (name, maxLength = 12) => {
  if (!name || name.length <= maxLength) return name;
  return name.substring(0, maxLength - 1) + '…';
};

/** Convert Leaflet Point / [x,y] to a plain pair. */
function toPointPair(point) {
  if (!point) return [0, 0];
  if (Array.isArray(point)) return point;
  return [point.x, point.y];
}

/** DivIcon pixel layout — must match ICONS (iconSize, iconAnchor, popupAnchor). */
function getDeviceMarkerLayout(device) {
  if (isLgBandDeviceType(device)) {
    return {
      width: lgBandIconSize[0],
      height: lgBandIconSize[1],
      iconAnchor: lgBandIconAnchor,
      popupAnchor: lgBandPopupAnchor,
    };
  }
  const vehicleType = device.Vehicletype || device.vehicleType || device.device_type;
  const selectedIcon = getDeviceIcon(vehicleType);
  const [width, height] = toPointPair(selectedIcon.options.iconSize);
  return {
    width,
    height,
    iconAnchor: toPointPair(selectedIcon.options.iconAnchor),
    popupAnchor: toPointPair(selectedIcon.options.popupAnchor),
  };
}

const createDeviceDivIcon = (device, showNameplates, shouldShowLabel, isSelected) => {
  const isLgBand = isLgBandDeviceType(device);
  const isSos = isLgBand && isLgBandSosDevice(device);
  const { width: markerPx, height: markerHeight, iconAnchor, popupAnchor } = getDeviceMarkerLayout(device);

  let iconUrl;
  if (isLgBand) {
    iconUrl = LG_BAND_ICON_URL;
  } else {
    const vehicleType = device.Vehicletype || device.vehicleType || device.device_type;
    const selectedIcon = getDeviceIcon(vehicleType);
    iconUrl = selectedIcon.options.iconUrl;
    if (selectedIcon === ICONS.default || !iconUrl || iconUrl.includes('marker-icon')) {
      console.warn(`⚠️ Device "${device.displayName || device.name || device.device_id}" has unrecognized type "${vehicleType}". Using vehicle icon as fallback.`);
      iconUrl = ICONS.vehicle.options.iconUrl;
    }
  }
  let statusLabel = '';
  let markerStatusClass = 'status-stopped';

  if (device.status && typeof device.status === 'string') {
    const lowerCaseStatus = device.status.toLowerCase();
    let statusClass = 'stopped';
    if (['running', 'online', 'active'].includes(lowerCaseStatus)) {
      statusClass = 'running';
      markerStatusClass = 'status-running';
    }
    else if (['inactive', 'unknown'].includes(lowerCaseStatus)) {
      statusClass = 'inactive';
      markerStatusClass = 'status-inactive';
    }
    
    statusLabel = `<div class="status-label ${statusClass}"></div>`;
  } else {
    statusLabel = '<div class="status-label stopped"></div>';
  }

  const deviceName = device.displayName || device.name || device.device_id || 'Unknown';
  const truncatedName = truncateDeviceName(deviceName, 12);
  const nameplateHTML = showNameplates && shouldShowLabel ? `<div class="device-nameplate" title="${deviceName}">${truncatedName}</div>` : '';

  const hiddenLabelClass = showNameplates && !shouldShowLabel ? 'has-hidden-label' : '';
  const lgBandClass = isLgBand ? 'lg-band-marker' : '';
  const sosClass = isSos ? 'lg-band-sos' : '';
  const sosEmitHtml = isSos
    ? `<div class="lg-band-sos-emit" aria-label="SOS alert">
        <span class="lg-band-sos-radial" aria-hidden="true"></span>
        <span class="lg-band-sos-ring lg-band-sos-ring--inner" aria-hidden="true"></span>
        <span class="lg-band-sos-ring lg-band-sos-ring--middle" aria-hidden="true"></span>
        <span class="lg-band-sos-ring lg-band-sos-ring--outer" aria-hidden="true"></span>
        <span class="lg-band-sos-shockwave" aria-hidden="true"></span>
        <span class="lg-band-sos-pulse-label lg-band-sos-label">SOS</span>
      </div>`
    : '';

  const iconBodyHtml = isLgBand
    ? `<div class="lg-band-icon-slot"><img src="${iconUrl}" alt="${device.device_type}" style="pointer-events: auto;" />${statusLabel}</div>`
    : `<img src="${iconUrl}" alt="${device.device_type}" style="pointer-events: auto;" />${statusLabel}`;

  return new L.DivIcon({
    html: `<div class="device-marker zoom-independent ${markerStatusClass} ${hiddenLabelClass} ${lgBandClass} ${sosClass} ${isSelected ? 'selected-marker' : ''}" style="cursor: pointer; width: ${markerPx}px; height: ${markerHeight}px;">
      ${sosEmitHtml}
      ${nameplateHTML}
      <div class="icon-wrapper" style="pointer-events: auto;">
        ${iconBodyHtml}
      </div>
    </div>`,
    className: [
      showNameplates && shouldShowLabel ? 'show-nameplates' : '',
      isSos ? 'lg-band-sos-marker-icon' : '',
    ]
      .filter(Boolean)
      .join(' '),
    iconSize: [markerPx, markerHeight],
    iconAnchor,
    popupAnchor,
  });
};

function popupGetStatusClass(status) {
  if (!status) return 'status-inactive';
  const lowerStatus = String(status).toLowerCase();
  if (lowerStatus === 'active') return 'status-active';
  if (lowerStatus === 'online' || lowerStatus === 'running') return 'status-running';
  if (lowerStatus === 'inactive' || lowerStatus === 'offline' || lowerStatus === 'unknown' || lowerStatus === 'stopped') {
    return 'status-inactive';
  }
  return 'status-stopped';
}

function popupFormatDateTime(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { hour12: true });
  return `${day}-${month}-${year} ${time}`;
}

function popupFormatIsoField(iso) {
  return iso ? popupFormatDateTime(iso) : 'N/A';
}

/** Same coordinate resolution as map follow logic — strings, nested location, coordinates. */
function getDeviceMarkerPositionTuple(device) {
  if (!device) return null;
  let lat =
    device.last_latitude ??
    device.latitude ??
    device.location?.latitude ??
    device.coordinates?.lat;
  let lng =
    device.last_longitude ??
    device.longitude ??
    device.location?.longitude ??
    device.coordinates?.lng;
  if (typeof lat === 'string') lat = parseFloat(lat);
  if (typeof lng === 'string') lng = parseFloat(lng);
  if (lat == null || lng == null) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return [lat, lng];
}

const DeviceMarker = React.memo(({ device: deviceProp, showNameplates, shouldShowLabel }) => {
  const dispatch = useDispatch();
  const map = useMap();
  const popupRef = React.useRef(null);
  const markerRef = React.useRef(null);

  const activeTrailDeviceId = useSelector(state => state.deviceHistory.activeDeviceId);
  const trailMode = useSelector(state => state.deviceHistory.trailMode);
  const shouldNavigateToSelectedDevice = useSelector(state => state.ui.shouldNavigateToSelectedDevice);

  // Use the already-filtered map device whenever possible. The fallback keeps
  // older callers working without making every marker scan Redux arrays.
  const device = deviceProp ?? null;

  const stableMarkerPosRef = React.useRef(null);
  const markerPosition = useMemo(() => {
    const t = getDeviceMarkerPositionTuple(device);
    if (!t) {
      stableMarkerPosRef.current = null;
      return null;
    }
    const prev = stableMarkerPosRef.current;
    if (prev && prev[0] === t[0] && prev[1] === t[1]) {
      return prev;
    }
    stableMarkerPosRef.current = t;
    return t;
  }, [device]);

  // Select selected device id BEFORE any early returns (hooks must not be conditional)
  const selectedDeviceId = useSelector(state => state.devices.selectedDevice?.device_id);

  const isSelected = selectedDeviceId && (selectedDeviceId === (device && device.device_id));

  const repositionDevicePopup = useCallback(() => {
    const layer = markerRef.current;
    const popup = layer?.getPopup?.();
    if (!popup) return;
    if (typeof popup.update === 'function') {
      popup.update();
    } else if (typeof popup._updatePosition === 'function') {
      popup._updatePosition();
    }
  }, []);

  /** Open popup on this marker (not map.openOn) and re-anchor after pan/zoom/layout. */
  const openDevicePopup = useCallback(() => {
    const layer = markerRef.current;
    if (!layer || !markerPosition) return;

    const open = () => {
      try {
        if (typeof layer.openPopup === 'function') {
          layer.openPopup();
        } else if (popupRef.current && map) {
          popupRef.current.openOn(map);
        }
        repositionDevicePopup();
      } catch (err) {
        console.warn('[DeviceMarker] openPopup failed:', err);
      }
    };

    const runAfterLayout = () => {
      requestAnimationFrame(() => {
        open();
        requestAnimationFrame(repositionDevicePopup);
      });
    };

    if ((map._panAnim && map._panAnim._inProgress) || (map._zoomAnim && map._zoomAnim._inProgress)) {
      map.once('moveend', runAfterLayout);
      return;
    }
    runAfterLayout();
  }, [map, markerPosition, repositionDevicePopup]);

  // Open popup only when this marker *becomes* selected (e.g. sidebar / URL). Do not depend on
  // `device` reference — Firestore/RT updates create a new object every tick and would re-run
  // open, which fights Leaflet and causes the popup to flicker open/closed.
  const prevIsSelectedRef = React.useRef(false);
  const pendingOpenAfterNavigateRef = React.useRef(false);
  React.useEffect(() => {
    const becameSelected = Boolean(isSelected) && !prevIsSelectedRef.current;
    prevIsSelectedRef.current = Boolean(isSelected);

    if (!isSelected) {
      pendingOpenAfterNavigateRef.current = false;
      return undefined;
    }

    if (becameSelected) {
      pendingOpenAfterNavigateRef.current = true;
    }

    if (!pendingOpenAfterNavigateRef.current || !markerPosition) {
      return undefined;
    }

    // Sidebar selection may trigger map flyTo first. Queue popup until navigation settles.
    if (shouldNavigateToSelectedDevice) {
      return undefined;
    }

    pendingOpenAfterNavigateRef.current = false;
    let cancelled = false;
    const scheduleOpen = () => {
      if (cancelled) return;
      openDevicePopup();
    };

    let timer;
    const onMoveEnd = () => {
      timer = setTimeout(scheduleOpen, 80);
    };

    if ((map._panAnim && map._panAnim._inProgress) || (map._zoomAnim && map._zoomAnim._inProgress)) {
      map.once('moveend', onMoveEnd);
    } else {
      timer = setTimeout(scheduleOpen, 150);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      map.off('moveend', onMoveEnd);
    };
  }, [isSelected, markerPosition, map, shouldNavigateToSelectedDevice, openDevicePopup]);

  const handleMarkerClick = useCallback((e) => {
    if (!device) return;
    if (isLgBandDeviceType(device)) {
      const mapDeviceId = device.device_id || device.id;
      const docId = device.adafruit_firestore_doc_id != null ? String(device.adafruit_firestore_doc_id) : null;
      dispatch(startLiveSession({
        deviceId: mapDeviceId,
        adafruitFirestoreDocId: docId,
        startedAtMs: Date.now(),
        initialPoint: markerPosition ? {
          lat: markerPosition[0],
          lng: markerPosition[1],
          timestamp: Date.now(),
          status: device.status || null,
        } : null,
      }));
      dispatch(setSelectedDevice(device));
      const name = device.displayName || device.name || device.device_id || 'LG Band';
      dispatch(
        addAlert({
          message: `Live Tracking User: ${name}`,
          type: 'live_tracking',
          deviceStatus: device.status,
        })
      );
      openDevicePopup();
      return;
    }
    if (e?.originalEvent) {
      L.DomEvent.stopPropagation(e.originalEvent);
    }
    dispatch(clearSelectedDevice());
    openDevicePopup();
  }, [device, dispatch, openDevicePopup, markerPosition]);

  const handleMarkerDoubleClick = useCallback((e) => {
    if (!device) return;
    if (e?.originalEvent) {
      L.DomEvent.stopPropagation(e.originalEvent);
    }
    if (isLgBandDeviceType(device)) {
      return;
    }
    dispatch(clearHistory());
    dispatch(setSelectedDevice(device));
    openDevicePopup();
  }, [device, dispatch, openDevicePopup]);

  const markerIcon = useMemo(
    () => device ? createDeviceDivIcon(device, showNameplates, shouldShowLabel, isSelected) : null,
    [device, showNameplates, shouldShowLabel, isSelected]
  );

  const markerEventHandlers = useMemo(
    () => ({
      click: handleMarkerClick,
      dblclick: handleMarkerDoubleClick,
      popupopen: () => {
        requestAnimationFrame(repositionDevicePopup);
      },
    }),
    [handleMarkerClick, handleMarkerDoubleClick, repositionDevicePopup]
  );

  // Don't render if device is not found or has no coordinates (place AFTER hooks)
  // Also don't render if device_id is a placeholder or city/state marker
  if (!device || !markerIcon || !markerPosition ||
      !device.device_id ||
      device.device_id === '__default__' ||
      device.device_id.startsWith('city_') ||
      device.device_id.startsWith('state_')) {
    return null;
  }

  const isLgBand = device.device_type === 'lg_band' || device.device_type === 'adafruit';
  const coordLat = device.last_latitude;
  const coordLng = device.last_longitude;
  const coordStr =
    typeof coordLat === 'number' && typeof coordLng === 'number' && !Number.isNaN(coordLat) && !Number.isNaN(coordLng)
      ? `${coordLat.toFixed(6)}, ${coordLng.toFixed(6)}`
      : 'N/A';

  const lgBandDeviceId =
    device.deviceId || device.adafruit_hardware_device_id || device.adafruit_firestore_doc_id || null;
  const lgBandLastSyncRaw =
    device.last_updated_iso ||
    device.latest_timestamp_iso ||
    (typeof device.lastSeen === 'string' ? device.lastSeen : null) ||
    device.last_seen ||
    (device.lastSeen?.seconds ? new Date(device.lastSeen.seconds * 1000).toISOString() : null);

  return (
    <ZoomIndependentMarker
      ref={markerRef}
      key={device.unique_key || device.device_id}
      position={markerPosition}
      icon={markerIcon}
      eventHandlers={markerEventHandlers}
    >
      <Popup
        ref={popupRef}
        className="custom-popup"
        autoPan
        autoPanPadding={[50, 50]}
        keepInView
      >
        <h4 title={device.displayName || device.name}>{device.displayName || device.name}</h4>
        <hr />
        <div className="popup-content">
          {isLgBand ? (
            <>
              <div className="info-row">
                <span className="info-label">Device ID</span>
                <span className="info-value">{lgBandDeviceId != null ? String(lgBandDeviceId) : 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className={`status-badge ${popupGetStatusClass(device.status)}`}>
                  {device.status || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Voltage</span>
                <span className="info-value">
                  {device.latest_voltage != null ? `${Number(device.latest_voltage).toFixed(2)} V` : 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Last sync</span>
                <span className="info-value">{lgBandLastSyncRaw ? popupFormatIsoField(lgBandLastSyncRaw) : 'N/A'}</span>
              </div>
            </>
          ) : (
            <>
              <div className="info-row">
                <span className="info-label">Type</span>
                <span className="info-value">
                  {device.device_type === 'triton' ? 'Triton Device' : device.device_type === 'lg_band' ? 'LG Band' : device.device_type}
                </span>
              </div>
              {device.device_type === 'triton' && device.triton_type && (
                <div className="info-row">
                  <span className="info-label">Triton Type</span>
                  <span className="info-value">{device.triton_type}</span>
                </div>
              )}
              {device.deviceId && device.device_type === 'triton' && (
                <div className="info-row">
                  <span className="info-label">Device ID</span>
                  <span className="info-value">{device.deviceId}</span>
                </div>
              )}
              <div className="info-row">
                <span className="info-label">Status</span>
                <span className={`status-badge ${popupGetStatusClass(device.status)}`}>
                  {device.status || 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Battery</span>
                <span className={`battery-indicator ${device.battery_level > 50 ? 'battery-high' : device.battery_level > 20 ? 'battery-medium' : 'battery-low'}`}>
                  🔋 {device.battery_level != null ? `${device.battery_level}%` : 'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Last Seen</span>
                <span className="info-value">
                  {device.last_seen ? popupFormatDateTime(device.last_seen) :
                   typeof device.lastSeen === 'string' ? popupFormatDateTime(device.lastSeen) :
                   device.lastSeen?.seconds ? popupFormatDateTime(device.lastSeen.seconds * 1000) :
                   'N/A'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Coordinates</span>
                <span className="info-value">{coordStr}</span>
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
          {(device.parentVehicleType || (isLgBand && device.adafruit_firestore_doc_id)) && (() => {
            const thisDeviceId = device.device_id || device.id;
            const isTrailActive = trailMode === 'history' && activeTrailDeviceId === thisDeviceId;
            const lgTrail =
              isLgBand &&
              device.adafruit_firestore_doc_id != null &&
              String(device.adafruit_firestore_doc_id) !== '';
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isTrailActive) {
                    dispatch(clearHistory());
                    return;
                  }
                  if (lgTrail) {
                    dispatch(
                      fetchLgBandTodayReadingsHistory({
                        mapDeviceId: thisDeviceId,
                        adafruitFirestoreDocId: device.adafruit_firestore_doc_id,
                      })
                    )
                      .unwrap()
                      .then((payload) => {
                        if (!payload.points?.length) {
                          dispatch(addAlert('No LG Band GPS readings for today yet.'));
                        }
                      })
                      .catch((err) => {
                        const msg = typeof err === 'string' ? err : err?.message || 'Could not load trail for today.';
                        dispatch(addAlert(msg));
                      });
                    return;
                  }
                  dispatch(
                    fetchDeviceHistory({
                      deviceId: thisDeviceId,
                      parentVehicleType: device.parentVehicleType,
                      maxPoints: 50,
                    })
                  );
                }}
                style={{
                  flex: 1, padding: '7px 10px', fontSize: '0.78rem', fontWeight: 600,
                  background: isTrailActive
                    ? 'linear-gradient(135deg, #dc2626, #ef4444)'
                    : 'linear-gradient(135deg, #004aad, #0062e6)',
                  color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                }}
              >
                {isTrailActive ? '✕ Hide Trail' : '📍 History Trail'}
              </button>
            );
          })()}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const url = `${window.location.origin}${window.location.pathname}?device=${encodeURIComponent(device.device_id || device.id)}&lat=${device.last_latitude}&lng=${device.last_longitude}&zoom=18`;
              navigator.clipboard.writeText(url).then(() => {
                e.target.textContent = '✓ Copied!';
                setTimeout(() => { e.target.textContent = '🔗 Share'; }, 2000);
              }).catch(() => {
                window.prompt('Copy this link:', url);
              });
            }}
            style={{
              flex: 1, padding: '7px 10px', fontSize: '0.78rem', fontWeight: 600,
              background: '#f1f5f9', color: '#334155',
              border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}
          >
            🔗 Share
          </button>
        </div>
      </Popup>
    </ZoomIndependentMarker>
  );
});

export default DeviceMarker;
